/**
 * Normalise rich-text HTML produced by the admin editor (TipTap) before it is
 * rendered on the public site via dangerouslySetInnerHTML.
 *
 * TipTap output can look tidy in the CMS but render poorly on the public page:
 *  - blank lines become empty <p></p> → large vertical gaps
 *  - list items are wrapped as <li><p>…</p></li> → odd bullet spacing
 *  - a heading typed in the body can be an <h1>, duplicating the page's real H1
 *  - a wide table has no scroll container → the whole page scrolls sideways on
 *    a phone. Authors can add one by hand, but TipTap strips it the next time
 *    the body is saved, so the wrapper is applied here at render instead.
 *
 * These transforms are surgical and safe for the hand-authored editorial bodies
 * too (which use <li>text</li> and <h2>/<h3>, never in-body <h1> or empty <p>).
 */
export function cleanTiptapHtml(html: string | null | undefined): string {
  if (!html) return "";
  let h = html;
  // Drop empty paragraphs (blank lines / stray <br> / &nbsp; only).
  h = h.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");
  // Strip a leading <br> inside a paragraph.
  h = h.replace(/<p>(?:\s|&nbsp;)*<br\s*\/?>\s*/gi, "<p>");
  // Unwrap a single <p> inside a list item (TipTap wraps li content in <p>).
  h = h.replace(/<li>\s*<p>([\s\S]*?)<\/p>\s*<\/li>/gi, "<li>$1</li>");
  // Demote any in-body <h1> to <h2> — the page header already owns the only H1.
  h = h.replace(/<h1(\s[^>]*)?>/gi, "<h2>").replace(/<\/h1>/gi, "</h2>");
  h = wrapTablesForScroll(h);
  return h.trim();
}

/**
 * Give every table its own horizontal scroll container so a wide one scrolls
 * inside its box instead of pushing the page sideways on a narrow screen.
 *
 * A table that fits is unaffected: the container only scrolls once the table's
 * minimum content width exceeds it, so desktop layout does not change.
 *
 * Existing hand-added wrappers are unwrapped first, so a body that already has
 * them ends up with exactly one container per table rather than two nested.
 * That also makes this idempotent across repeated renders.
 */
const SCROLL_WRAPPER_STYLE = "overflow-x:auto;-webkit-overflow-scrolling:touch";

function wrapTablesForScroll(html: string): string {
  return html
    .replace(
      /<div[^>]*overflow-x\s*:\s*auto[^>]*>\s*(<table[\s\S]*?<\/table>)\s*<\/div>/gi,
      "$1",
    )
    .replace(
      /<table[\s\S]*?<\/table>/gi,
      (table) => `<div style="${SCROLL_WRAPPER_STYLE}">${table}</div>`,
    );
}
